/*
 * <zendesk-contact-form>: Artika contact form → Zendesk anonymous Requests API.
 *
 * Flow on submit:
 *   honeypot check → validation → minimum time-to-submit → upload files (one POST each to /api/v2/uploads.json)
 *   → POST /api/v2/requests.json → success panel. Any failure shows the fallback box (email + phone).
 *
 * No customer-facing text lives in this file. Every string comes from the JSON block that the section renders
 * from theme locale keys ([data-zd-config] → "strings"). Placeholders in those strings use {name} style.
 *
 * Zendesk docs:
 *   https://developer.zendesk.com/api-reference/ticketing/tickets/ticket-requests/#create-request
 *   https://developer.zendesk.com/documentation/ticketing/managing-tickets/creating-and-managing-requests/#creating-anonymous-requests
 *   https://developer.zendesk.com/api-reference/ticketing/tickets/ticket-attachments/#upload-files
 */
(() => {
  if (customElements.get('zendesk-contact-form')) return;

  const ALLOWED_TYPES = /^(image\/(jpeg|png|webp|gif|heic|heif)|application\/pdf)$/i;
  const ALLOWED_EXT = /\.(jpe?g|png|webp|gif|heic|heif|pdf)$/i;
  const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
  const SUBDOMAIN_RE = /^[a-z0-9][a-z0-9-]*$/;
  const MIME_BY_EXT = { heic: 'image/heic', heif: 'image/heif', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', pdf: 'application/pdf' };

  // Shopify's | t filter HTML-escapes strings that take variables (n'est → n&#39;est). Decode once so
  // textContent shows real characters.
  const decode = (value) => {
    if (typeof value === 'string') {
      const ta = document.createElement('textarea');
      ta.innerHTML = value;
      return ta.value;
    }
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decode(v)]));
    }
    return value;
  };

  const fill = (str, vars) => String(str || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));

  class ZendeskContactForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.cfg = JSON.parse(this.querySelector('[data-zd-config]').textContent);
      this.t = decode(this.cfg.strings);
      this.form = this.querySelector('[data-zd-form]');
      this.panel = this.querySelector('[data-zd-form-panel]');
      this.success = this.querySelector('[data-zd-success]');
      this.summary = this.querySelector('[data-zd-summary]');
      this.sendError = this.querySelector('[data-zd-send-error]');
      this.submitBtn = this.querySelector('[data-zd-submit]');
      this.submitLabel = this.querySelector('[data-zd-submit-label]');
      this.status = this.querySelector('[data-zd-status]');
      this.reason = this.querySelector('[data-zd-reason]');
      this.otherField = this.querySelector('[data-zd-other]');
      this.fileInput = this.querySelector('[data-zd-files]');
      this.fileList = this.querySelector('[data-zd-file-list]');
      this.hp = this.querySelector('[data-zd-hp]');
      this.suggest = this.querySelector('[data-zd-suggest]');
      this.suggestList = this.querySelector('[data-zd-suggest-list]');
      this.guide = this.querySelector('[data-zd-guide]');
      this.guideText = this.querySelector('[data-zd-guide-text]');
      this.guideCta = this.querySelector('[data-zd-guide-cta]');
      this.viewed = new Map(); // article id -> title, for articles the customer opened

      this.files = [];
      this.submitting = false;
      this.attempted = false;
      this.startedAt = Date.now();

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.reason.addEventListener('change', () => { this.toggleOther(); this.renderGuidance(); this.renderSuggestions(); });
      if (this.guideCta) this.guideCta.addEventListener('click', () => this.onGuidanceClick());
      const revalidate = (e) => {
        if (!this.attempted) return;
        this.validateField(e.target.name);
        if (!this.form.querySelector('.is-invalid')) this.showSummary(''); // all fixed: hide the summary box
      };
      ['input', 'focusout', 'change'].forEach((type) => this.form.addEventListener(type, revalidate));
      if (this.fileInput) {
        this.fileInput.addEventListener('change', () => this.addFiles(this.fileInput.files));
        this.fileList.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-zd-remove]');
          if (btn) this.removeFile(Number(btn.dataset.zdRemove));
        });
      }
      this.querySelector('[data-zd-again]').addEventListener('click', () => this.reset());
      this.toggleOther();
      this.renderGuidance(); // a subject restored by the browser after Back
    }

    /* ---------- fields ---------- */

    field(name) {
      return this.form.querySelector(`[data-zd-field="${name}"]`);
    }

    value(name) {
      const el = this.form.elements[name];
      return el ? String(el.value || '').trim() : '';
    }

    /* ---------- suggested articles ---------- */

    suggestionsFor(code) {
      // Titles come from a dynamic | t key, which HTML-escapes them (Artika's → Artika&#39;s).
      return ((this.cfg.suggestions || {})[code] || [])
        .filter((a) => a && a.url && a.title)
        .map((a) => ({ ...a, title: decode(a.title) }));
    }

    renderSuggestions() {
      if (!this.suggest) return;
      const items = this.suggestionsFor(this.reason.value);
      this.suggestList.replaceChildren(...items.map((a) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'zd-suggest__link';
        link.href = a.url;
        link.target = '_blank'; // keeps the form and what was typed
        link.rel = 'noopener';
        link.textContent = a.title;
        link.addEventListener('click', () => this.onSuggestionClick(a));
        li.append(link);
        return li;
      }));
      this.suggest.hidden = items.length === 0;
    }

    /* ---------- subject guidance ---------- */

    // Short help for some subjects, e.g. "Register a product" links to the registration form
    // (a registration there sends a confirmation and logs it for the team, no ticket needed).
    renderGuidance() {
      if (!this.guide) return;
      const g = decode(((this.cfg.guidance || {})[this.reason.value]) || null);
      const text = g && g.text ? String(g.text).trim() : '';
      this.guideText.textContent = text;
      const showCta = Boolean(text && g.cta && g.url);
      this.guideCta.hidden = !showCta;
      if (showCta) {
        this.guideCta.textContent = g.cta;
        this.guideCta.href = g.url;
      }
      this.guide.hidden = !text;
      this.guide.classList.toggle('zd-guide--action', showCta);
    }

    onGuidanceClick() {
      const detail = { reason: this.reason.value, url: this.guideCta.href, lang: this.cfg.lang, market: this.cfg.market };
      this.dispatchEvent(new CustomEvent('zendesk-contact:guidance-click', { bubbles: true, detail }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'zendesk_contact_guidance_click', contact_reason: detail.reason });
    }

    onSuggestionClick(article) {
      this.viewed.set(article.id, article.title);
      const detail = { articleId: article.id, title: article.title, reason: this.reason.value, lang: this.cfg.lang, market: this.cfg.market };
      this.dispatchEvent(new CustomEvent('zendesk-contact:suggestion-click', { bubbles: true, detail }));
      // Analytics hook (GA4 / GTM), only if a dataLayer exists on the page.
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'zendesk_contact_suggestion_click', zendesk_article_id: article.id, contact_reason: detail.reason });
    }

    toggleOther() {
      if (!this.otherField) return;
      const show = this.reason.value === 'other';
      this.otherField.hidden = !show;
      this.form.elements.subject_other.required = show;
      if (!show) this.setError('subject_other', '');
    }

    setError(name, message) {
      const wrap = this.field(name);
      if (!wrap) return;
      const err = wrap.querySelector('[data-zd-error]');
      const input = wrap.querySelector('input:not([type="hidden"]), select, textarea');
      const hasError = Boolean(message);
      wrap.classList.toggle('is-invalid', hasError);
      err.textContent = message || '';
      err.hidden = !hasError;
      if (input) {
        if (hasError) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
        const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && id !== err.id);
        if (hasError) ids.push(err.id);
        if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
        else input.removeAttribute('aria-describedby');
      }
    }

    validateField(name) {
      const t = this.t;
      switch (name) {
        case 'name':
        case 'message':
          this.setError(name, this.value(name) ? '' : t.required);
          return !!this.value(name);
        case 'email': {
          const v = this.value('email');
          const msg = !v ? t.required : EMAIL_RE.test(v) ? '' : t.email;
          this.setError('email', msg);
          return !msg;
        }
        case 'reason':
          this.setError('reason', this.reason.value ? '' : t.required);
          return !!this.reason.value;
        case 'subject_other': {
          if (this.reason.value !== 'other') return true;
          this.setError('subject_other', this.value('subject_other') ? '' : t.required);
          return !!this.value('subject_other');
        }
        default:
          return true;
      }
    }

    validate() {
      const order = ['reason', 'subject_other', 'name', 'email', 'message']; // same order as on the page
      const invalid = order.filter((n) => !this.validateField(n));
      if (this.fileErrors && this.fileErrors.length) invalid.push('files');
      if (invalid.length) {
        this.showSummary(this.t.summary);
        const first = this.field(invalid[0]);
        const target = first && first.querySelector('input:not([type="hidden"]), select, textarea');
        if (target) target.focus();
        return false;
      }
      this.showSummary('');
      return true;
    }

    showSummary(message) {
      this.summary.textContent = message || '';
      this.summary.hidden = !message;
    }

    /* ---------- files ---------- */

    isAllowed(file) {
      return ALLOWED_TYPES.test(file.type || '') || ALLOWED_EXT.test(file.name || '');
    }

    addFiles(list) {
      const t = this.t;
      const errors = [];
      for (const file of Array.from(list || [])) {
        if (this.files.some((f) => f.name === file.name && f.size === file.size)) continue;
        if (!this.isAllowed(file)) { errors.push(fill(t.fileType, { name: file.name })); continue; }
        if (file.size > this.cfg.maxMb * 1024 * 1024) { errors.push(fill(t.fileSize, { name: file.name, size: this.cfg.maxMb })); continue; }
        if (this.files.length >= this.cfg.maxFiles) { errors.push(fill(t.fileCount, { max: this.cfg.maxFiles })); break; }
        this.files.push(file);
      }
      this.fileInput.value = '';
      this.fileErrors = [];
      this.setError('files', errors.join(' '));
      this.renderFiles();
    }

    removeFile(index) {
      this.files.splice(index, 1);
      this.setError('files', '');
      this.renderFiles();
      (this.fileList.querySelector('[data-zd-remove]') || this.fileInput).focus();
    }

    renderFiles() {
      if (!this.fileList) return;
      this.fileList.replaceChildren(...this.files.map((file, i) => {
        const li = document.createElement('li');
        li.className = 'zd-files__item';
        const name = document.createElement('span');
        name.className = 'zd-files__name';
        name.textContent = file.name;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'zd-files__remove';
        btn.dataset.zdRemove = String(i);
        btn.setAttribute('aria-label', fill(this.t.removeFile, { name: file.name }));
        btn.innerHTML = '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        li.append(name, btn);
        return li;
      }));
      this.fileList.hidden = this.files.length === 0;
    }

    /* ---------- submit ---------- */

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return; // no double submit
      this.attempted = true;
      this.sendError.hidden = true;

      // Honeypot: a bot filled the invisible field. Pretend it worked, send nothing.
      if (this.hp && this.hp.value) { this.showSuccess(); return; }

      if (!this.validate()) return;

      // Minimum time-to-submit: bots post instantly. A person just presses Send again.
      if ((Date.now() - this.startedAt) / 1000 < Number(this.cfg.minSeconds || 0)) {
        this.startedAt = 0; // the second press always goes through
        this.showSummary(this.t.tooFast);
        this.submitBtn.focus();
        return;
      }

      const base = this.baseUrl();
      this.setBusy(true);
      try {
        const captchaToken = await this.getCaptchaToken(); // eslint-disable-line no-unused-vars
        const tokens = [];
        for (let i = 0; i < this.files.length; i += 1) {
          this.announce(fill(this.t.uploading, { current: i + 1, total: this.files.length }));
          tokens.push(await this.uploadFile(base, this.files[i]));
        }
        this.announce(this.t.sending);
        const payload = this.buildPayload(tokens);
        const result = await this.createRequest(base, payload);
        this.showSuccess();
        this.dispatchEvent(new CustomEvent('zendesk-contact:submitted', {
          bubbles: true,
          detail: { requestId: result && result.request ? result.request.id : null, reason: this.reason.value, market: this.cfg.market, lang: this.cfg.lang },
        }));
      } catch (err) {
        console.warn('[zendesk-contact-form]', err);
        this.sendError.hidden = false;
        this.sendError.focus();
      } finally {
        this.setBusy(false);
      }
    }

    baseUrl() {
      const sub = String(this.cfg.subdomain || '').toLowerCase();
      if (!SUBDOMAIN_RE.test(sub)) throw new Error('Zendesk subdomain setting is empty or invalid');
      return `https://${sub}.zendesk.com`;
    }

    async uploadFile(base, file) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const type = file.type || MIME_BY_EXT[ext] || 'application/binary';
      const res = await fetch(`${base}/api/v2/uploads.json?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { 'Content-Type': type },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status}) for ${file.name}`);
      const data = await res.json();
      if (!data || !data.upload || !data.upload.token) throw new Error('Upload response had no token');
      return data.upload.token;
    }

    async createRequest(base, payload) {
      const res = await fetch(`${base}/api/v2/requests.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status < 200 || res.status >= 300) {
        let detail = '';
        try { detail = JSON.stringify(await res.json()); } catch (e) { /* not JSON */ }
        throw new Error(`Create request failed (${res.status}) ${detail}`);
      }
      try { return await res.json(); } catch (e) { return null; }
    }

    /*
     * Builds the documented Create Request body:
     *   { request: { requester: { name, email, locale }, subject, comment: { body, uploads }, tags } }
     * Note: "tags" is not in Zendesk's documented Request properties, so Zendesk may drop it for anonymous
     * requests. The same codes are written on the last line of the comment so a Zendesk trigger can tag
     * the ticket from the text (see DEV-BRIEF.md).
     */
    buildPayload(tokens) {
      const t = this.t.ticket;
      const cfg = this.cfg;
      const reasonCode = this.reason.value;
      const reasonLabel = reasonCode === 'other'
        ? this.value('subject_other')
        : (this.reason.options[this.reason.selectedIndex] || {}).text || reasonCode;
      const order = this.value('order');
      const pageUrl = window.location.origin + window.location.pathname;

      let subject = order ? fill(t.subjectWithOrder, { subject: reasonLabel, order }) : reasonLabel;
      if (cfg.testMode) subject = `[TEST] ${subject}`;
      subject = subject.slice(0, 150);

      const tags = ['web_contact_form', `lang_${cfg.lang}`, `market_${cfg.market}`, `reason_${reasonCode}`];
      if (this.suggestionsFor(reasonCode).length) tags.push('suggestions_shown');
      if (this.viewed.size) tags.push('suggestion_clicked');
      if (cfg.testMode) tags.push('test');

      const line = (label, value) => fill(t.line, { label, value });
      const details = [
        line(t.name, this.value('name')),
        line(t.email, this.value('email')),
        order ? line(t.order, order) : null,
        line(t.subject, reasonLabel),
        line(t.page, pageUrl),
        line(t.market, t.marketName),
        line(t.language, t.languageName),
        tokens.length ? line(t.attachments, String(tokens.length)) : null,
        this.viewed.size ? line(t.articlesViewed, [...this.viewed.values()].join('; ')) : null,
      ].filter(Boolean);

      const body = [this.value('message'), '', '———', ...details, '', `[${tags.join(' ')}]`].join('\n');

      const comment = { body };
      if (tokens.length) comment.uploads = tokens;

      const requester = { name: this.value('name'), email: this.value('email') };
      if (cfg.locale) requester.locale = cfg.locale;

      return { request: { requester, subject, comment, tags } };
    }

    /*
     * ===== OPTIONAL CAPTCHA HOOK (not wired up) =====
     * To add Cloudflare Turnstile or hCaptcha:
     *   1. Load the provider script on this page only, render the widget into [data-zd-captcha] and unhide it.
     *   2. Return the widget's token here (e.g. turnstile.getResponse(widgetId)) and block the submit when empty.
     * Read this first: Zendesk does not verify captcha tokens. Because the browser calls Zendesk directly, a bot
     * can skip the form and call the API itself. A captcha only adds real protection if the request goes through
     * a small server (e.g. a Cloudflare Worker or a Shopify app proxy) that verifies the token and then calls
     * Zendesk. Without that, Zendesk's own "Verify anonymous requests" step is the spam control.
     */
    async getCaptchaToken() {
      return null;
    }

    /* ---------- states ---------- */

    setBusy(busy) {
      this.submitting = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.submitBtn.setAttribute('aria-disabled', String(busy));
      this.submitBtn.classList.toggle('is-loading', busy);
      this.submitLabel.textContent = busy ? this.t.sending : this.t.submit;
      if (!busy) this.announce('');
    }

    announce(message) {
      if (this.status) this.status.textContent = message || '';
    }

    showSuccess() {
      this.panel.hidden = true;
      this.success.hidden = false;
      const heading = this.success.querySelector('[data-zd-success-heading]');
      heading.focus({ preventScroll: true });
      const top = this.getBoundingClientRect().top + window.scrollY - 120;
      if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    reset() {
      this.form.reset();
      this.files = [];
      this.fileErrors = [];
      this.renderFiles();
      ['name', 'email', 'reason', 'subject_other', 'message', 'files'].forEach((n) => this.setError(n, ''));
      this.showSummary('');
      this.sendError.hidden = true;
      this.attempted = false;
      this.toggleOther();
      this.viewed.clear();
      this.renderGuidance();
      this.renderSuggestions();
      this.startedAt = Date.now();
      this.success.hidden = true;
      this.panel.hidden = false;
      this.reason.focus(); // subject is the first field
    }
  }

  customElements.define('zendesk-contact-form', ZendeskContactForm);
})();
